/**
 * Shared helpers for the product-admin template/version selectors (Deal
 * Attributes, Questionnaire, Estimate, Rate Card, Resource Model).
 *
 * The selectors all follow the same rules:
 *  - R1: when no workspace-level version exists, fall back to the system one.
 *  - R2: once a workspace-level version exists, hide the system one.
 *  - R3: version labels are formatted consistently as `v{n} · {Status}`,
 *        with ` · System` appended only for system-scope rows.
 */

export const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  saved: 'Saved',
  published: 'Published',
  archived: 'Archived',
};

export interface VersionRowLike {
  scope: string; // 'system' | 'workspace'
  version: string | number;
  status: string; // 'draft' | 'saved' | 'published' | 'archived'
  isDefault?: boolean;
  isActive?: boolean;
}

/** R3: canonical dropdown label, e.g. "v2 · Draft" or (system fallback) "v1 · Published · System". */
export function formatVersionLabel(row: {
  version: string | number;
  status: string;
  scope: string;
}): string {
  const status = STATUS_LABELS[row.status] ?? row.status;
  const base = `v${row.version} · ${status}`;
  return row.scope === 'system' ? `${base} · System` : base;
}

/**
 * R1/R2: within each group (keyed by `keyOf`), drop system-scope rows whenever
 * that group also has at least one workspace-scope row. System rows survive
 * only as the fallback when the workspace owns nothing for that key.
 */
export function filterByScope<T extends { scope: string }>(
  rows: T[],
  keyOf: (row: T) => string
): T[] {
  const workspaceKeys = new Set(rows.filter((r) => r.scope === 'workspace').map(keyOf));
  return rows.filter((r) => r.scope !== 'system' || !workspaceKeys.has(keyOf(r)));
}

/**
 * Label for a per-deal artifact version (estimate / SOW) in the read-only
 * history dropdown, e.g. `v2.1 · Jun 25 · 320h · edited (Current)`. Fields are
 * optional so the same helper serves both artifacts (SOW has no hours).
 */
export function formatDealVersionLabel(input: {
  version: string | null;
  timestamp: string;
  totalHours?: number | null;
  edited?: boolean;
  isCurrent?: boolean;
}): string {
  const parts: string[] = [`v${input.version ?? '1.0'}`];
  const d = new Date(input.timestamp);
  if (!Number.isNaN(d.getTime())) {
    parts.push(d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
  }
  if (input.totalHours != null) parts.push(`${Math.round(input.totalHours)}h`);
  if (input.edited) parts.push('edited');
  let label = parts.join(' · ');
  if (input.isCurrent) label += ' (Current)';
  return label;
}

/**
 * R1: choose the active row from a (already scope-filtered) list - prefer a
 * workspace default/active, then any workspace row, then a system default/active,
 * then the first available.
 */
export function pickActive<T extends VersionRowLike>(rows: T[]): T | undefined {
  if (rows.length === 0) return undefined;
  const isActive = (r: T) => Boolean(r.isDefault || r.isActive);
  return (
    rows.find((r) => r.scope === 'workspace' && isActive(r)) ??
    rows.find((r) => r.scope === 'workspace') ??
    rows.find((r) => isActive(r)) ??
    rows[0]
  );
}
